import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store/useStore';
import { useAuthStore } from '../store/useAuthStore';
import { feedbackService, FeedbackCategory } from '../services/feedback.service';
import { X, Loader2, Send } from 'lucide-react';

const CATEGORIES: FeedbackCategory[] = ['Bug', 'Idée', 'Amélioration', 'Question'];

export function FeedbackModal() {
  const { t } = useTranslation();
  const isFeedbackModalOpen = useStore((state) => state.isFeedbackModalOpen);
  const closeFeedbackModal = useStore((state) => state.closeFeedbackModal);
  const { session } = useAuthStore();

  const [category, setCategory] = useState<FeedbackCategory>('Idée');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  // Reset state when the modal opens
  useEffect(() => {
    if (isFeedbackModalOpen) {
      setCategory('Idée');
      setMessage('');
      setIsSubmitting(false);
      setError(null);
      setIsSuccess(false);
    }
  }, [isFeedbackModalOpen]);

  if (!isFeedbackModalOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setIsSubmitting(true);
    setError(null);
    setIsSuccess(false);

    try {
      await feedbackService.submitFeedback({
        category,
        message: message.trim(),
        userEmail: session?.user?.email,
        userId: session?.user?.id,
        route: window.location.pathname + window.location.hash,
        timestamp: new Date().toISOString(),
      });
      // Show success message and clear form
      setIsSuccess(true);
      setMessage('');
    } catch (err: any) {
      setError(err.message || "Une erreur inattendue s'est produite.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900/50">
          <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-400" />
            {t('feedback.modal_title')}
          </h2>
          <button
            onClick={closeFeedbackModal}
            className="p-2 text-zinc-400 hover:text-zinc-100 transition-colors rounded-lg hover:bg-zinc-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        {isSuccess ? (
          <div className="p-8 flex flex-col items-center justify-center text-center gap-4">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mb-2">
              <Send className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-zinc-100">{t('feedback.success_title')}</h3>
            <p className="text-sm text-zinc-400 max-w-sm">{t('feedback.success_message')}</p>
            <button
              onClick={closeFeedbackModal}
              className="mt-4 px-6 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm font-medium rounded-lg transition-colors"
            >
              {t('common.close')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-6">
            {error && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm text-amber-200 leading-relaxed">
                <strong className="block font-semibold mb-1 text-amber-400">
                  {t('errors.attention')}
                </strong>
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                {t('feedback.type_label')}
              </label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                      category === cat
                        ? 'bg-blue-500/20 text-blue-300 border-blue-500/50'
                        : 'bg-zinc-800/50 text-zinc-400 border-zinc-700/50 hover:bg-zinc-800 hover:text-zinc-200'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                {t('feedback.message_label')}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('feedback.message_placeholder')}
                className="w-full h-32 bg-black/40 border border-zinc-800 rounded-lg p-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 resize-none transition-all"
                required
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-800">
              <button
                type="button"
                onClick={closeFeedbackModal}
                className="px-4 py-2 text-sm font-medium text-zinc-400 hover:text-zinc-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !message.trim()}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common.sending')}
                  </>
                ) : (
                  t('common.send')
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
